/*
 * pdp-personalisation.js
 * -------------------------------------------------------------------------
 * PRODUCT-PAGE half of the "Name to be printed" guard. The cart-page half
 * lives in assets/cart-personalisation.js, which is only loaded on the cart -
 * which is why the product page had no validation at all.
 *
 * WHICH PRODUCTS GET VALIDATED
 * -------------------------------------------------------------------------
 * King (ymq) Product Options is the single source of truth. On a personalised
 * product it renders:
 *
 *   <div class="ymq-options-box"
 *        data-validate="required|minchar:1|maxchar:20"
 *        data-label="Name to be printed"
 *        data-name="properties[Name to be printed]"> ... <input class="ymq-input-text">
 *
 * On a product that needs no name - a book, say - King renders NOTHING: zero
 * .ymq-options-box elements. So this script keys off King's own markup and is
 * a complete no-op on non-personalised products. There is no hardcoded product
 * or collection list here to drift out of sync with King's admin.
 *
 * Only boxes whose data-validate contains "required" are enforced; an optional
 * King field (e.g. a gift message) is left alone.
 *
 * TIMING - the thing that makes this subtle
 * -------------------------------------------------------------------------
 * King renders its options ASYNCHRONOUSLY, well after DOMContentLoaded. This
 * script is deferred, so when it first runs a personalised product looks
 * exactly like a non-personalised one: zero boxes. Any "nothing here, give up"
 * shortcut at startup therefore disables the guard permanently. The observer is
 * always attached for that reason; the gate itself no-ops while no boxes exist.
 *
 * WHAT IT GUARDS
 * -------------------------------------------------------------------------
 *   Add to cart      -> intercept the click, show an inline error, focus field.
 *                      (Deliberately NOT dimmed: a greyed-out ATC with no
 *                      explanation reads as a broken page.)
 *   Buy it now /     -> up to 1.3.0: GATED (unclickable + dimmed). Since 1.3.1
 *   express /           they stay LIVE and the click is intercepted instead
 *   Razorpay /          (see below).
 *   Addly upsell
 *
 * 1.3.1 — NOTHING IS DISABLED ANY MORE
 * -------------------------------------------------------------------------
 * Two things used to grey out the buttons: this file's gate on Buy Now, and
 * King's own best-options.js, which sets inline `pointer-events:none` on
 * Add to cart and the payment buttons while its validation fails. Shoppers
 * read a dead button as a broken page. Now: this gate only RELEASES, and
 * cart-personalise.css overrides King's inline style with
 * `pointer-events:auto !important`. A click with a missing name is caught
 * here, in the WINDOW capture phase (Razorpay's magic-shopify.js binds on
 * document capture and loads first — a document-capture guard runs after it
 * and cannot stop the portal; measured on the cart page, TM-417). The field
 * turns red, a "Please enter name to be printed" popup appears
 * (window.ZNKing.notify from king-personalisation.js) and the cursor lands in
 * the first empty field.
 * -------------------------------------------------------------------------
 */
(function () {
  var BOX_SEL = '.ymq-options-box[data-validate*="required"]';
  var FIELD_SEL = 'input.ymq-input-text, textarea.ymq-input-text, [name^="ymq["]';
  var ATC_SEL = 'button[name="add"], .product-form__submit';
  // Paths that bypass the cart. Gated rather than click-intercepted.
  // Razorpay's REAL trigger on the PDP is .magic-buy-btn (confirmed in the
  // rendered theme). It does NOT contain the string "magic-checkout", so a
  // [class*="magic-checkout"] selector misses it entirely.
  var BYPASS_SEL = [
    '.magic-buy-btn',                 // Razorpay Magic Checkout "Buy Now" (PDP)
    '.magic-buy-btn__parent',
    '.shopify-payment-button',        // native dynamic checkout (Magic adds .magic-hide)
    '.shopify-payment-button__button',
    '.additional-checkout-buttons',
    '.addly_bundle_addToCart',
    '.addly_quickView_addToCart',
    '.n9-fbt-btn-atc',
    '.n9-quickview-btn-custom'
  ].join(',');

  // NEVER gate these. They are the Razorpay PORTAL itself - modal, backdrop,
  // iframe - plus its config/script containers. They are not triggers, and
  // dimming or disabling them would leave a customer stranded inside a checkout
  // that had legitimately opened. A broad [class*="razorpay"] selector hits all
  // three, which is why this exclusion exists.
  var PORTAL_SEL = [
    '.razorpay-container',
    '.razorpay-backdrop',
    '.razorpay-checkout-frame',
    '#magic-checkout-data-container',
    '#magic-merchant-js'
  ].join(',');

  function boxes() {
    return Array.prototype.slice.call(document.querySelectorAll(BOX_SEL));
  }

  function fieldOf(box) {
    return box.querySelector(FIELD_SEL);
  }

  function labelOf(box) {
    return box.getAttribute('data-label') || 'this field';
  }

  // A field still showing King's own hint text counts as UNFILLED.
  //
  // King's admin has both a "placeholder" and a "default value". On tem10
  // (Rakhi hampers), tem11 (Bag Charms + Hair Clips) and tem12 (Hair Clips
  // Alphabets) the hint was entered into BOTH, so King renders it as a real
  // value: the box looks like grey hint text, but `.value` is literally
  // "Max 8 characters can fit in the charm".
  //
  // Every required-check then passes - King's OWN validator included - so
  // nothing is ever gated and no error is ever shown. King then strips that
  // value again when it builds the line-item properties, so the order arrives
  // with NO name at all. That is the whole mechanism behind the Bag Charm and
  // Hair Clip orders that reached production blank, and it is why those three
  // templates are exactly the affected ones.
  //
  // The clean permanent fix is to clear "default value" in the King admin and
  // leave the hint in the placeholder only. This check makes the storefront
  // safe whether or not that happens.
  function isUnfilled(f) {
    var v = String(f.value == null ? '' : f.value).trim();
    if (!v) return true;
    var hint = String(f.placeholder == null ? '' : f.placeholder).trim();
    return !!hint && v === hint;
  }

  // Every required King field that is still empty.
  function missing() {
    return boxes().filter(function (b) {
      var f = fieldOf(b);
      return f && isUnfilled(f);
    });
  }

  // ---- inline error -------------------------------------------------------
  function errorEl(box) {
    var el = box.querySelector('[data-pdp-cp-error]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-pdp-cp-error', '');
      el.style.cssText =
        'color:#d72c0d;font-size:0.85rem;margin-top:0.35rem;line-height:1.3;';
      box.appendChild(el);
    }
    return el;
  }

  function showError(box, on) {
    var el = errorEl(box);
    box.classList.toggle('zn-cp-invalid', !!on);
    if (on) {
      el.textContent = 'Please add “' + labelOf(box) + '” before continuing.';
      el.hidden = false;
      var f = fieldOf(box);
      if (f) f.style.borderColor = '#d72c0d';
    } else {
      el.hidden = true;
      var g = fieldOf(box);
      if (g) g.style.borderColor = '';
    }
  }

  // Amber "REQUIRED" card while a required field is still empty (styled in
  // cart-personalise.css, same look as the cart). Red wins while invalid.
  function syncEmptyState() {
    boxes().forEach(function (b) {
      var f = fieldOf(b);
      var empty = !!f && isUnfilled(f);
      b.classList.toggle('zn-cp-empty', empty && !b.classList.contains('zn-cp-invalid'));
    });
  }

  var MISSING_MSG = 'Please enter name to be printed';

  // Popup + cursor into the first empty field. Falls back to a plain alert
  // if king-personalisation.js has not loaded on this page.
  function complain(box) {
    var f = fieldOf(box);
    if (!f) return;
    if (window.ZNKing && typeof window.ZNKing.notify === 'function') {
      window.ZNKing.notify(MISSING_MSG, f);
    } else {
      f.scrollIntoView({ behavior: 'smooth', block: 'center' });
      f.focus();
      try { window.alert(MISSING_MSG); } catch (e) {}
    }
  }

  // ---- gating (release-only since 1.3.1) ----------------------------------
  // gateEl(el, false) is kept so a control gated by an older cached copy of
  // this script is restored to clickable.
  var GATE = 'data-pdp-cp-gated';

  function gateEl(el, off) {
    if (!el) return;
    if (off) {
      if (!el.hasAttribute(GATE)) {
        el.setAttribute(GATE, '1');
        el.dataset.pdpPrevPe = el.style.pointerEvents || '';
        el.dataset.pdpPrevOp = el.style.opacity || '';
      }
      el.style.pointerEvents = 'none';
      el.style.opacity = '0.4';
      el.setAttribute('aria-disabled', 'true');
    } else if (el.hasAttribute(GATE)) {
      el.style.pointerEvents = el.dataset.pdpPrevPe || '';
      el.style.opacity = el.dataset.pdpPrevOp || '';
      el.setAttribute('aria-disabled', 'false');
      el.removeAttribute(GATE);
      delete el.dataset.pdpPrevPe;
      delete el.dataset.pdpPrevOp;
    }
  }

  function syncGates() {
    if (!boxes().length) return;              // not a personalised product (yet)
    Array.prototype.slice.call(document.querySelectorAll(BYPASS_SEL)).forEach(function (el) {
      // Never touch the portal (see PORTAL_SEL).
      if (el.matches(PORTAL_SEL) || el.closest(PORTAL_SEL)) return;
      gateEl(el, false);
    });
    syncEmptyState();
  }

  var timer = null;
  function scheduleSync() {
    if (timer) return;
    timer = setTimeout(function () { timer = null; syncGates(); }, 120);
  }

  // ---- events -------------------------------------------------------------
  document.addEventListener('input', function (e) {
    if (!e.target.closest) return;
    var box = e.target.closest(BOX_SEL);
    if (!box) return;
    showError(box, false);
    syncGates();
  });

  // A field pre-filled with King's hint should not look "already answered". The
  // moment the shopper focuses it, clear the hint so they type into an empty box
  // and the value they leave behind is genuinely theirs.
  document.addEventListener('focusin', function (e) {
    if (!e.target.closest) return;
    var box = e.target.closest(BOX_SEL);
    if (!box) return;
    var f = fieldOf(box);
    if (!f || f !== e.target) return;
    var v = String(f.value == null ? '' : f.value).trim();
    var hint = String(f.placeholder == null ? '' : f.placeholder).trim();
    if (hint && v === hint) {
      f.value = '';
      syncGates();
    }
  });

  // THE defence for Add to cart, Buy Now, express and upsell buttons. WINDOW
  // capture: the only phase that runs before Razorpay's document-capture
  // handler (see header). A click inside the open portal is never touched.
  window.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest(PORTAL_SEL)) return;        // never touch an open portal
    var btn = e.target.closest(ATC_SEL) || e.target.closest(BYPASS_SEL);
    if (!btn) return;
    if (!boxes().length) return;              // non-personalised product: never block

    var miss = missing();
    if (!miss.length) return;                 // all good - let Shopify / Razorpay handle it

    e.preventDefault();
    e.stopPropagation();
    boxes().forEach(function (b) { showError(b, false); });
    miss.forEach(function (b) { showError(b, true); });
    syncGates();
    complain(miss[0]);
  }, true);

  // Enter-key / programmatic submit of the product form.
  window.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches) return;
    if (!form.matches('form[action*="/cart/add"], form[data-type="add-to-cart-form"]')) return;
    if (!boxes().length) return;
    var miss = missing();
    if (!miss.length) return;
    e.preventDefault();
    e.stopPropagation();
    miss.forEach(function (b) { showError(b, true); });
    syncGates();
    complain(miss[0]);
  }, true);

  function init() {
    // NB: do NOT early-return when there are no boxes yet. King renders its
    // options ASYNCHRONOUSLY, well after DOMContentLoaded, so at init time a
    // personalised product looks identical to a non-personalised one. Bailing
    // here meant the observer never attached and the gate never ran - Buy Now
    // stayed live for the whole session. syncGates() is itself a no-op while
    // there are no boxes, so it is safe to always attach.
    syncGates();
    try {
      // King re-renders its options on variant change, and the express buttons
      // mount late, so re-apply on DOM change. Debounced.
      new MutationObserver(scheduleSync)
        .observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) { /* click guards still apply */ }
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();